<script setup lang="ts">
import type { ValidationRule } from 'vuetify';
import type { FieldSize } from '@/components/fields/KTextField.vue';
import type * as openapi from '@/helpers/api/types/openapiOverrides';

import { computed, nextTick, ref, unref, watch } from 'vue';
import { storeToRefs } from 'pinia';

import NoDataSelectVListItem from '@/components/items/NoDataSelectVListItem.vue';
import useAutocompleteField from '@/composables/useAutocompleteField';
import { useStaticStore } from '@/stores/useStaticStore';

import fieldStyles from '@/styles/field.module.scss';
import listItemStyles from '@/styles/listItem.module.scss';

export interface Props {
  modelValue?: number | null;
  size?: FieldSize;
  loading?: boolean;
  label?: string;
  rules?: ValidationRule[];
  disabled?: boolean;
}
const props = withDefaults(defineProps<Props>(), {
  modelValue: undefined,
  size: 'md',
  loading: false,
  label: 'Driver',
  rules: () => [],
  disabled: false,
});
const emit = defineEmits<{
  'update:modelValue': [value: number | null];
}>();

const staticStore = useStaticStore();
const { getDrivers: driverList } = storeToRefs(staticStore);
const fieldValue = ref(props.modelValue);
const search = ref('');
const field = ref<HTMLFormElement | null>(null);
const minSearchLength = 1;
const { handleEnterKeydown } = useAutocompleteField(fieldValue, search, field);
const sizeClass = computed(() => 'size-' + props.size);

type DriverItem = openapi.GetCatalogDrivers_200[number];

function getDriverItemTitle(item?: Partial<DriverItem> | null): string {
  const code = item?.code;
  const fullName = item?.full_name;

  if (typeof code !== 'number' || typeof fullName !== 'string') {
    return '-';
  }

  return `${code}, ${fullName}`;
}

const customFilter = (
  value: string,
  query: string,
  item?: { raw: { code: number; full_name: string } },
) => {
  if (!query || !item) return true;

  const searchQuery = query.toLowerCase();
  const code = String(item.raw.code).toLowerCase();
  const fullName = item.raw.full_name.toLowerCase();

  return code.includes(searchQuery) || fullName.includes(searchQuery);
};

const rules = computed(() => {
  const drivers = unref(driverList);

  function existingDriverValidationRule(value: unknown): boolean | string {
    if (value === null || value === undefined) {
      return true;
    }

    if (!drivers.some((driver) => driver.code === value)) {
      return 'Selected driver does not exist';
    }

    return true;
  }

  return [...props.rules, existingDriverValidationRule];
});

watch(
  () => props.modelValue,
  (newValue) => {
    fieldValue.value = newValue;
  },
  { immediate: true },
);

watch(driverList, async () => {
  if (fieldValue.value !== null && fieldValue.value !== undefined) {
    await nextTick();
    // field ref is a Vuetify component at runtime (typed as HTMLFormElement)
    (
      field.value as unknown as { resetValidation?: () => void }
    ).resetValidation?.();
  }
});

watch(fieldValue, (newValue) => {
  emit('update:modelValue', newValue ?? null);
});
</script>

<template>
  <v-autocomplete
    ref="field"
    v-model="fieldValue"
    v-model:search="search"
    :label="props.label"
    :items="driverList"
    :class="[
      'g-v-text-field',
      $style.driverAutocompleteField,
      fieldStyles[sizeClass],
    ]"
    variant="outlined"
    :base-color="$style.fieldBaseColor"
    :color="$style.fieldColor"
    clearable
    menu-icon="mdi-chevron-down"
    validate-on="eager"
    :disabled="props.disabled"
    :loading="props.loading"
    :rules="rules"
    :custom-filter="customFilter"
    data-testid="driverAutocompleteField"
    item-value="code"
    :item-title="getDriverItemTitle"
    @keydown.enter="handleEnterKeydown"
  >
    <template #no-data>
      <NoDataSelectVListItem>
        <template v-if="!search || search.length < minSearchLength">
          Please enter {{ minSearchLength }} or more characters
        </template>
        <template v-else>No such data</template>
      </NoDataSelectVListItem>
    </template>
    <template #item="{ props, item }">
      <v-list-item
        :class="listItemStyles.listItem"
        :active-class="listItemStyles.listItemActive"
        :ripple="false"
        v-bind="props"
        :title="getDriverItemTitle(item.raw)"
      />
    </template>
  </v-autocomplete>
</template>

<!-- prettier-ignore -->
<style module lang="scss">
@use '@/styles/colors';
@use '@/styles/variables';

:export {
  fieldBaseColor: colors.$color-border-input-hovered;
  fieldColor: colors.$color-brand-800;
  chipColor: colors.$color-brand-200;
}

.driverAutocompleteField {
  :global {
    .v-field__input input {
      margin: 0;
      padding: 0;
    }
    .v-autocomplete__selection {
      margin-top: 0;
      margin-inline-end: 6px !important;
    }
    .v-field__outline {
      --v-field-border-width: 1px !important;
    }
  }
}

.chip {
  color: colors.$color-text-primary !important;

  :global {
    .v-chip__prepend {
      font-size: 10px;
    }
    .v-chip__content {
      font-size: 16px;
    }
    .v-chip__close {
      background: transparent;
      border: 0;
      margin: 0;
      padding: 0;
      font-size: 16px;
    }
  }
}
</style>
